<template>
  <div v-loading="loading" class="live-editor-page">
    <Crud
      class="list-page-crud"
      :filter-items="filterItems"
      :value="filters"
      :pagination="pagination"
      @input="onFiltersInput"
    >
      <template #filters-actions>
        <el-button size="small" type="primary" :loading="saving" :disabled="!dirtyCount" @click="saveDirty">
          保存{{ dirtyCount ? `（${dirtyCount}）` : '' }}
        </el-button>
      </template>
      <template #table>
        <div class="live-editor-table-wrap">
          <Table
            :columns="tableColumns"
            :data="tableRows"
            row-key="_rowKey"
            border
            height="100%"
            :row-class-name="rowClassName"
          >
          <template #col-date="{ row }">
            <el-date-picker
              :value="row.date || ''"
              type="date"
              size="mini"
              clearable
              :append-to-body="true"
              placeholder="选择日期"
              value-format="yyyy-MM-dd"
              class="live-editor-cell__control live-editor-cell__control--date"
              :disabled="Boolean(row.closeActual)"
              @input="onComplaintDate(row, $event)"
            />
          </template>
          <template #col-yc="{ row }">
            <LiveEditorPatchTextarea
              :value="row.patch.yc"
              placeholder="异常投诉"
              :disabled="Boolean(row.closeActual)"
              @input="onPatch(row, 'yc', $event)"
            />
          </template>
          <template #col-deptP="{ row }">
            <el-select
              :value="row.patch.deptP || ''"
              size="mini"
              filterable
              clearable
              :popper-append-to-body="true"
              placeholder="生产部门"
              class="live-editor-cell__control"
              @change="onPatch(row, 'deptP', $event || '')"
            >
              <el-option v-for="name in deptOptions" :key="name" :label="name" :value="name" />
            </el-select>
          </template>
          <template #col-fzr="{ row }">
            <el-select
              :value="fzrList(row)"
              size="mini"
              multiple
              collapse-tags
              filterable
              clearable
              :popper-append-to-body="true"
              placeholder="负责人"
              class="live-editor-cell__control live-editor-cell__control--multi"
              @change="onFzrChange(row, $event)"
            >
              <el-option v-for="name in fzrOptions" :key="name" :label="name" :value="name" />
            </el-select>
          </template>
          <template #col-cause="{ row }">
            <LiveEditorPatchTextarea
              :value="row.patch.cause"
              placeholder="原因"
              :disabled="Boolean(row.closeActual)"
              @input="onPatch(row, 'cause', $event)"
            />
          </template>
          <template #col-temp="{ row }">
            <LiveEditorPatchTextarea
              :value="row.patch.temp"
              placeholder="临时对策"
              :disabled="Boolean(row.closeActual)"
              @input="onPatch(row, 'temp', $event)"
            />
          </template>
          <template #col-permanent="{ row }">
            <LiveEditorPatchTextarea
              :value="row.patch.permanent"
              placeholder="永久对策"
              :disabled="Boolean(row.closeActual)"
              @input="onPatch(row, 'permanent', $event)"
            />
          </template>
          <template #col-reply="{ row }">
            <LiveEditorOpStageCell
              :plan-text="replyPlanDate(row)"
              label="回复"
              :value="row.replyActual"
              :done="Boolean(row.replyActual)"
              :interactive="opCellCanOpen('reply', row)"
              @click="onOpCellClick('reply', row)"
            />
          </template>
          <template #col-defect="{ row }">
            <LiveEditorOpStageCell
              :plan-text="replyPlanDate(row)"
              label="完成"
              :value="row.defectActual"
              :done="Boolean(row.defectActual)"
              :loading="switchBusyKey(row) === 'defect'"
              :interactive="opCellCanOpen('defect', row)"
              @click="onOpCellClick('defect', row)"
            />
          </template>
          <template #col-close="{ row }">
            <LiveEditorOpStageCell
              :plan-text="closePlanDate(row)"
              label="结案"
              :value="row.closeActual"
              :done="Boolean(row.closeActual)"
              :loading="switchBusyKey(row) === 'close'"
              :interactive="opCellCanOpen('close', row)"
              @click="onOpCellClick('close', row)"
            />
          </template>
          </Table>
          <div class="live-editor-row-ctrl">
            <div class="live-editor-row-ctrl__btns">
              <button
                type="button"
                class="live-editor-row-btn live-editor-row-btn--remove"
                title="删除最后一行（仅未保存的新增行）"
                :disabled="!canRemoveLastRow"
                @click="removeLastRow"
              >
                −
              </button>
              <button
                type="button"
                class="live-editor-row-btn live-editor-row-btn--add"
                title="添加一行"
                @click="addRow"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </template>
    </Crud>

    <LiveEditorOpStageDialog
      :visible.sync="opDialog.visible"
      :mode="opDialog.mode"
      :plan-default="opDialog.planDefault"
      :meta="opDialog.meta"
      :readonly="opDialog.readonly"
      :plan-readonly="opDialog.planReadonly"
      :confirming="opDialog.confirming"
      :show-close-case="opDialog.showCloseCase"
      :initial="opDialog.initial"
      @confirm="onOpDialogConfirm"
      @close-case="onCloseCaseFromDialog"
    />
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

import type { LiveEditorDraftRow } from '@/components/live-editor/types';
import LiveEditorPatchTextarea from '@/components/live-editor/patch-textarea.vue';
import LiveEditorOpStageCell from '@/components/live-editor/op-stage-cell.vue';
import LiveEditorOpStageDialog, {
  type LiveEditorOpDialogForm,
  type LiveEditorOpDialogMeta,
  type LiveEditorOpDialogMode,
} from '@/components/live-editor/op-stage-dialog.vue';
import { Crud, Table } from '@/components/crud';
import {
  bulkSaveLiveCases,
  closeLiveCase,
  fetchLiveCases,
  patchLiveCase,
  initialLiveCasesLoadState,
  peekLiveCasesCache,
  markLiveDefect,
  undoLiveDefect,
  type LiveCase,
} from '@/api/live-case';
import {
  addCalendarDays,
  LIVE_CLOSE_PLAN_DAYS,
  LIVE_REPLY_PLAN_DAYS,
  liveClosePlanDateForRow,
  liveReplyPlanDate,
  liveReplyPlanDateForRow,
  type LiveCasePatch,
} from '@/domain/live-case';
import { SIM_TODAY } from '@/utils/time';

import { liveEditorColumns } from './columns';
import { stagePlanDateForOp, stagePlanDefault, stagePlanRowFromDraft } from './plan-defaults';
import { joinFzrList, parseFzrList, uniqueFzrOptions } from './fzr';
import {
  buildLiveEditorFilterItems,
  filterLiveEditorRows,
  liveCaseMonth,
  liveCaseYear,
  normalizeLiveEditorFilters,
  pickDefaultYearMonth,
  type LiveEditorFilterState,
} from './filters';

function uniqueValues(values: Array<string | null | undefined>): string[] {
  const set = new Set<string>();
  values.forEach((value) => {
    const text = String(value || '').trim();
    if (text) set.add(text);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'zh-CN'));
}

function draftRowSignature(row: LiveEditorDraftRow): string {
  return JSON.stringify({
    date: row.date || '',
    replyActual: row.replyActual || '',
    replyPlanDate: row.replyPlanDate || '',
    patch: row.patch,
  });
}

function patchTextFilled(value: string | null | undefined): boolean {
  return String(value || '').trim().length > 0;
}

let newRowSeq = 0;

export default Vue.extend({
  name: 'LiveEditor',
  components: {
    Crud,
    Table,
    LiveEditorPatchTextarea,
    LiveEditorOpStageCell,
    LiveEditorOpStageDialog,
  },
  data() {
    const initial = initialLiveCasesLoadState();
    const emptyOpForm: LiveEditorOpDialogForm = {
      stagePlanDate: null,
      replyActual: null,
      cause: '',
      temp: '',
      defectActual: null,
      closeActual: null,
    };
    return {
      loading: initial.loading,
      saving: false,
      opDialog: {
        visible: false,
        mode: 'reply' as LiveEditorOpDialogMode,
        rowKey: '',
        planDefault: '',
        meta: {} as LiveEditorOpDialogMeta,
        readonly: false,
        planReadonly: false,
        confirming: false,
        showCloseCase: false,
        initial: { ...emptyOpForm },
      },
      emptyOpForm,
      filters: { q: '', year: [], month: [] } as LiveEditorFilterState,
      page: 1,
      pageSize: 50,
      draftRows: [] as LiveEditorDraftRow[],
      baselines: {} as Record<string, string>,
      switchBusy: {} as Record<string, 'defect' | 'close'>,
    };
  },
  computed: {
    filterItems() {
      return buildLiveEditorFilterItems(this.draftRows, this.filters);
    },
    filteredRows(): LiveEditorDraftRow[] {
      return filterLiveEditorRows(this.draftRows, this.filters);
    },
    tableRows(): LiveEditorDraftRow[] {
      const start = (this.page - 1) * this.pageSize;
      return this.filteredRows.slice(start, start + this.pageSize);
    },
    tableColumns() {
      return liveEditorColumns;
    },
    dirtyCount(): number {
      return this.draftRows.filter((row) => this.isRowDirty(row)).length;
    },
    deptOptions(): string[] {
      return uniqueValues(this.draftRows.map((row) => row.patch.deptP));
    },
    fzrOptions(): string[] {
      return uniqueFzrOptions(this.draftRows.map((row) => row.patch.fzr));
    },
    canRemoveLastRow(): boolean {
      const filtered = filterLiveEditorRows(this.draftRows, this.filters);
      if (!filtered.length) return false;
      const last = filtered[filtered.length - 1];
      return !last.id;
    },
    pagination() {
      const total = filterLiveEditorRows(this.draftRows, this.filters).length;
      return {
        page: this.page,
        pageSize: this.pageSize,
        total,
        layout: 'total, sizes, prev, pager, next',
        pageSizes: [30, 50, 100],
        onPageChange: (page: number) => {
          this.page = page;
        },
        onSizeChange: (size: number) => {
          this.pageSize = size;
          this.page = 1;
        },
      };
    },
  },
  created() {
    this.load();
  },
  methods: {
    rowClassName({ row }: { row: LiveEditorDraftRow }): string {
      return this.isRowDirty(row) ? 'live-editor-row--dirty' : '';
    },
    load() {
      const cached = peekLiveCasesCache();
      if (cached) this.hydrateFromLiveRows(cached);
      if (!cached) this.loading = true;
      fetchLiveCases()
        .then((rows) => this.hydrateFromLiveRows(rows))
        .finally(() => {
          this.loading = false;
        });
    },
    hydrateFromLiveRows(rows: LiveCase[]) {
      this.draftRows = rows.map((row) => this.toDraft(row));
      const defaults = pickDefaultYearMonth(this.draftRows);
      this.filters = normalizeLiveEditorFilters(this.draftRows, {
        q: '',
        year: defaults.year,
        month: defaults.month,
      });
      this.rebuildBaselines();
    },
    rebuildBaselines() {
      const next: Record<string, string> = {};
      this.draftRows.forEach((row) => {
        next[row._rowKey] = draftRowSignature(row);
      });
      this.baselines = next;
    },
    toDraft(row: LiveCase): LiveEditorDraftRow {
      return {
        _rowKey: row.id,
        id: row.id,
        date: row.date,
        replyActual: row.replyActual,
        replyPlanDate: row.replyPlanDate,
        defectActual: row.defectActual,
        closeActual: row.closeActual,
        patch: {
          yc: row.yc,
          deptP: row.deptP,
          fzr: row.fzr,
          planDate: row.planDate,
          cause: row.cause,
          temp: row.temp,
          permanent: row.permanent,
          deptC: row.deptC,
          jz: row.jz,
          pm: row.pm,
          lh: row.lh,
        },
      };
    },
    isRowDirty(row: LiveEditorDraftRow): boolean {
      if (row.create) {
        const p = row.patch;
        const hasContent = Boolean(
          (p.yc && p.yc.trim()) ||
            (p.deptP && p.deptP.trim()) ||
            (p.fzr && p.fzr.trim()) ||
            (p.planDate && p.planDate.trim()) ||
            (p.cause && p.cause.trim()) ||
            (p.temp && p.temp.trim()) ||
            (p.permanent && p.permanent.trim()),
        );
        if (!hasContent) return false;
      }
      return draftRowSignature(row) !== this.baselines[row._rowKey];
    },
    fzrList(row: LiveEditorDraftRow): string[] {
      return parseFzrList(row.patch.fzr);
    },
    onFzrChange(row: LiveEditorDraftRow, values: string[]) {
      this.onPatch(row, 'fzr', joinFzrList(values));
    },
    onComplaintDate(row: LiveEditorDraftRow, value: string | null) {
      const date = value || '';
      this.$set(row, 'date', date);
      this.syncAutoPlanDates(row, date);
    },
    syncAutoPlanDates(row: LiveEditorDraftRow, complaintDate: string) {
      if (!complaintDate || row.closeActual) return;
      this.$set(row, 'replyPlanDate', addCalendarDays(complaintDate, LIVE_REPLY_PLAN_DAYS));
      this.onPatch(row, 'planDate', addCalendarDays(complaintDate, LIVE_CLOSE_PLAN_DAYS));
    },
    setReplyPlanDate(row: LiveEditorDraftRow, value: string | null | undefined) {
      const text = String(value || '').trim();
      const fallback = stagePlanDefault('reply', stagePlanRowFromDraft(row));
      this.$set(row, 'replyPlanDate', !text || text === fallback ? null : text);
    },
    replyPlanDate(row: LiveEditorDraftRow): string {
      return liveReplyPlanDateForRow({ date: row.date || '', replyPlanDate: row.replyPlanDate }) || '—';
    },
    closePlanDate(row: LiveEditorDraftRow): string {
      return liveClosePlanDateForRow({ date: row.date || '', planDate: row.patch.planDate }) || '—';
    },
    opCellCanOpen(mode: LiveEditorOpDialogMode, row: LiveEditorDraftRow): boolean {
      if (mode === 'reply') return true;
      if (mode === 'defect') return Boolean(row.id);
      if (mode === 'close') return true;
      return false;
    },
    onOpCellClick(mode: LiveEditorOpDialogMode, row: LiveEditorDraftRow) {
      if (!this.opCellCanOpen(mode, row)) return;
      if (mode === 'reply') {
        if (!row.closeActual && !row.replyActual) {
          this.onReplyDate(row, SIM_TODAY);
          return;
        }
        this.openOpDialog(mode, row);
        return;
      }
      if (mode === 'defect') {
        if (!row.closeActual && !row.defectActual && row.id) {
          this.onDefectSwitch(row, true);
          return;
        }
        this.openOpDialog(mode, row);
        return;
      }
      if (!row.closeActual && this.canToggleClose(row)) {
        this.onCloseSwitch(row, true);
        return;
      }
      this.openOpDialog(mode, row);
    },
    opDialogRow(): LiveEditorDraftRow | null {
      if (!this.opDialog.rowKey) return null;
      return this.draftRows.find((item) => item._rowKey === this.opDialog.rowKey) || null;
    },
    openOpDialog(mode: LiveEditorOpDialogMode, row: LiveEditorDraftRow) {
      const readonly = Boolean(row.closeActual) && mode !== 'close';
      const planReadonly = Boolean(row.closeActual);
      const planRow = stagePlanRowFromDraft(row);
      const planDefault = stagePlanDefault(mode, planRow);
      const stagePlanDate = stagePlanDateForOp(mode, planRow);
      const showCloseCase = mode === 'close' && !row.closeActual && this.canToggleClose(row);
      this.opDialog = {
        ...this.opDialog,
        visible: true,
        mode,
        rowKey: row._rowKey,
        planDefault,
        meta: {
          date: row.date || '',
          yc: row.patch.yc || '',
          deptP: row.patch.deptP || '',
          fzr: row.patch.fzr || '',
          pm: row.patch.pm,
          lh: row.patch.lh,
        },
        readonly,
        planReadonly,
        confirming: false,
        showCloseCase,
        initial: {
          stagePlanDate: stagePlanDate || null,
          replyActual: row.replyActual || null,
          cause: row.patch.cause || '',
          temp: row.patch.temp || '',
          defectActual: row.defectActual || null,
          closeActual: row.closeActual || null,
        },
      };
    },
    onOpDialogConfirm(form: LiveEditorOpDialogForm) {
      const row = this.opDialogRow();
      if (!row) {
        this.opDialog.visible = false;
        return;
      }
      this.opDialog.confirming = true;
      if (this.opDialog.mode === 'reply') {
        if (!this.opDialog.readonly) this.applyReplyDialog(row, form);
        this.opDialog.confirming = false;
        this.opDialog.visible = false;
        return;
      }
      if (this.opDialog.mode === 'close') {
        if (!this.opDialog.planReadonly) this.applyCloseDialog(row, form);
        this.opDialog.confirming = false;
        this.opDialog.visible = false;
        return;
      }
      if (!this.opDialog.readonly) {
        this.applyDefectDialog(row, form);
        return;
      }
      this.opDialog.confirming = false;
      this.opDialog.visible = false;
    },
    applyReplyDialog(row: LiveEditorDraftRow, form: LiveEditorOpDialogForm) {
      this.setReplyPlanDate(row, form.stagePlanDate);
      this.$set(row.patch, 'cause', form.cause || '');
      this.$set(row.patch, 'temp', form.temp || '');
      this.onReplyDate(row, form.replyActual ? form.replyActual : null);
    },
    applyCloseDialog(row: LiveEditorDraftRow, form: LiveEditorOpDialogForm) {
      const plan =
        String(form.stagePlanDate || '').trim() ||
        stagePlanDefault('close', stagePlanRowFromDraft(row));
      if (plan) this.onPatch(row, 'planDate', plan);
    },
    defectDialogReplyPlanStored(
      row: LiveEditorDraftRow,
      stagePlanDate: string | null,
    ): string | null {
      const text = String(stagePlanDate || '').trim();
      const fallback = stagePlanDefault('reply', stagePlanRowFromDraft(row));
      return !text || text === fallback ? null : text;
    },
    applyDefectDialog(row: LiveEditorDraftRow, form: LiveEditorOpDialogForm) {
      const prevPlan = liveReplyPlanDateForRow({
        date: row.date || '',
        replyPlanDate: row.replyPlanDate,
      });
      const nextReplyPlan = this.defectDialogReplyPlanStored(row, form.stagePlanDate);
      const nextPlan = liveReplyPlanDateForRow({
        date: row.date || '',
        replyPlanDate: nextReplyPlan,
      });
      const planChanged = prevPlan !== nextPlan;
      const nextDate = form.defectActual ? form.defectActual : null;
      const prev = row.defectActual || null;
      const defectChanged = nextDate !== prev;

      if (!defectChanged && !planChanged) {
        this.opDialog.visible = false;
        return;
      }

      if (!row.id) {
        this.setReplyPlanDate(row, form.stagePlanDate);
        this.$set(row, 'defectActual', nextDate);
        this.opDialog.visible = false;
        return;
      }

      this.opDialog.confirming = true;
      const patch: LiveCasePatch = {};
      if (defectChanged) patch.defectActual = nextDate;
      if (planChanged) patch.replyPlanDate = nextReplyPlan;
      patchLiveCase(row.id, patch)
        .then((saved) => {
          this.applySavedRow(row, saved);
          this.opDialog.visible = false;
        })
        .catch((err: Error) => this.$message.error(err.message))
        .then(() => {
          this.opDialog.confirming = false;
        });
    },
    onPatch(row: LiveEditorDraftRow, field: keyof LiveCasePatch, value: string) {
      this.$set(row.patch, field, value);
      if (field === 'cause' || field === 'temp') {
        const hasReplyText =
          patchTextFilled(field === 'cause' ? value : row.patch.cause) ||
          patchTextFilled(field === 'temp' ? value : row.patch.temp);
        if (hasReplyText && !String(row.replyActual || '').trim()) {
          this.$set(row, 'replyActual', SIM_TODAY);
        }
      }
    },
    onReplyDate(row: LiveEditorDraftRow, value: string | null) {
      this.$set(row, 'replyActual', value || null);
    },
    onFiltersInput(value: LiveEditorFilterState) {
      this.filters = normalizeLiveEditorFilters(this.draftRows, value);
      this.page = 1;
    },
    addRow() {
      const today = SIM_TODAY;
      newRowSeq += 1;
      this.draftRows.push({
        _rowKey: `new:${newRowSeq}`,
        id: null,
        create: true,
        date: today,
        replyPlanDate: addCalendarDays(today, LIVE_REPLY_PLAN_DAYS),
        patch: { yc: '', deptP: '', fzr: '', planDate: addCalendarDays(today, LIVE_CLOSE_PLAN_DAYS) },
      });
      this.filters = normalizeLiveEditorFilters(this.draftRows, {
        ...this.filters,
        year: [liveCaseYear({ date: today })],
        month: [liveCaseMonth({ date: today })],
      });
      this.goToLastFilteredPage();
    },
    goToLastFilteredPage() {
      this.$nextTick(() => {
        const total = filterLiveEditorRows(this.draftRows, this.filters).length;
        const lastPage = Math.max(1, Math.ceil(total / this.pageSize));
        this.page = lastPage;
      });
    },
    removeLastRow() {
      const filtered = filterLiveEditorRows(this.draftRows, this.filters);
      const last = filtered[filtered.length - 1];
      if (!last) return;
      if (last.id) {
        this.$message.warning('已保存的投诉不能在此删除');
        return;
      }
      const remove = () => {
        const index = this.draftRows.indexOf(last);
        if (index >= 0) this.draftRows.splice(index, 1);
        this.goToLastFilteredPage();
      };
      if (!this.isRowDirty(last)) {
        remove();
        return;
      }
      this.$confirm('最后一行含有未保存内容，删除后不可恢复，是否继续？', '删除行', {
        type: 'warning',
        confirmButtonText: '删除',
        cancelButtonText: '取消',
      })
        .then(remove)
        .catch(() => undefined);
    },
    saveDirty() {
      const payload = this.draftRows
        .filter((row) => this.isRowDirty(row))
        .map((row) => ({
          id: row.id,
          create: row.create,
          date: row.create ? row.date : undefined,
          patch: {
            ...row.patch,
            ...(row.date ? { date: row.date } : {}),
            ...(row.replyActual !== undefined
              ? { replyActual: row.replyActual ? row.replyActual : null }
              : {}),
            ...(row.replyPlanDate !== undefined ? { replyPlanDate: row.replyPlanDate } : {}),
          },
        }));
      if (!payload.length) return;
      this.saving = true;
      bulkSaveLiveCases(payload)
        .then((rows) => {
          this.$message.success(`已保存 ${payload.length} 条`);
          this.draftRows = rows.map((row) => this.toDraft(row));
          const defaults = pickDefaultYearMonth(this.draftRows);
          if (!this.filters.year.length) this.filters.year = defaults.year;
          if (!this.filters.month.length) this.filters.month = defaults.month;
          this.rebuildBaselines();
        })
        .catch((err: Error) => this.$message.error(err.message))
        .then(() => {
          this.saving = false;
        });
    },
    switchBusyKey(row: LiveEditorDraftRow): 'defect' | 'close' | '' {
      return this.switchBusy[row._rowKey] || '';
    },
    setSwitchBusy(row: LiveEditorDraftRow, kind: 'defect' | 'close' | null) {
      if (!kind) {
        this.$delete(this.switchBusy, row._rowKey);
        return;
      }
      this.$set(this.switchBusy, row._rowKey, kind);
    },
    canToggleDefect(row: LiveEditorDraftRow): boolean {
      return Boolean(row.id) && !row.closeActual && !this.switchBusyKey(row);
    },
    canToggleClose(row: LiveEditorDraftRow): boolean {
      return Boolean(row.id) && !row.closeActual && !this.switchBusyKey(row);
    },
    onCloseCaseFromDialog() {
      const row = this.opDialogRow();
      if (!row || this.opDialog.confirming) return;
      this.opDialog.confirming = true;
      this.onCloseSwitch(row, true, { fromOpDialog: true });
    },
    applySavedRow(row: LiveEditorDraftRow, saved: LiveCase) {
      Object.assign(row, this.toDraft(saved));
      this.baselines[row._rowKey] = draftRowSignature(row);
    },
    onDefectSwitch(row: LiveEditorDraftRow, on: boolean) {
      if (!row.id || Boolean(row.closeActual)) return;
      const wasOn = Boolean(row.defectActual);
      if (on === wasOn) return;
      this.setSwitchBusy(row, 'defect');
      const action = on ? markLiveDefect : undoLiveDefect;
      action(row.id)
        .then((saved) => this.applySavedRow(row, saved))
        .catch((err: Error) => this.$message.error(err.message))
        .then(() => this.setSwitchBusy(row, null));
    },
    onCloseSwitch(
      row: LiveEditorDraftRow,
      on: boolean,
      options?: { fromOpDialog?: boolean },
    ) {
      if (!row.id || !on || Boolean(row.closeActual)) {
        if (options?.fromOpDialog) this.opDialog.confirming = false;
        return;
      }
      this.$confirm('结案后完成计划日与实际日不可再改，确定结案？', '结案确认', {
        type: 'warning',
        confirmButtonText: '结案',
        cancelButtonText: '取消',
      })
        .then(() => {
          this.setSwitchBusy(row, 'close');
          return closeLiveCase(row.id as string);
        })
        .then((saved) => {
          if (saved) this.applySavedRow(row, saved);
          if (options?.fromOpDialog) this.opDialog.visible = false;
        })
        .catch((err: unknown) => {
          if (err === 'cancel' || err === 'close') return;
          if (err instanceof Error) this.$message.error(err.message);
        })
        .then(() => {
          this.setSwitchBusy(row, null);
          if (options?.fromOpDialog) this.opDialog.confirming = false;
        });
    },
  },
});
</script>

<style lang="scss" scoped>
.live-editor-page {
  @include list-page-layout;
  height: calc(100vh - 56px - 40px);
  max-height: calc(100vh - 56px - 40px);
}

.live-editor-table-wrap {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

.live-editor-row-ctrl {
  flex-shrink: 0;
  padding: 6px 8px 4px;
  border-top: 1px solid $color-hairline;
  background: $color-surface;
}

.live-editor-row-ctrl__btns {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.live-editor-row-btn {
  width: 22px;
  height: 22px;
  padding: 0;
  border-radius: 2px;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &--add {
    border: 1px dashed $color-hairline;
    background: $color-surface-muted;
    color: $color-text-secondary;

    &:hover:not(:disabled) {
      border-color: $color-primary;
      color: $color-primary;
    }
  }

  &--remove {
    border: 1px solid rgba($color-danger, 0.35);
    background: rgba($color-danger, 0.06);
    color: $color-danger;

    &:hover:not(:disabled) {
      border-color: $color-danger;
      background: rgba($color-danger, 0.12);
    }
  }
}

.list-page-crud ::v-deep .live-editor-row--dirty td {
  background: #fffdf5;
}

.live-editor-cell__control {
  width: 100%;
}

.live-editor-cell__control--multi ::v-deep .el-select__tags {
  max-width: 100%;
}

.live-editor-cell__control--multi ::v-deep .el-tag {
  max-width: 100%;
}

.list-page-crud ::v-deep .el-table .cell .el-input,
.list-page-crud ::v-deep .el-table .cell .el-select,
.list-page-crud ::v-deep .el-table .cell .el-date-editor {
  width: 100%;
}

.list-page-crud ::v-deep .el-table .cell .el-input__inner {
  font-size: 12px;
}

.list-page-crud ::v-deep .el-table .cell .live-editor-cell__textarea textarea {
  font-size: 12px;
  line-height: 1.45;
  padding: 4px 6px;
  resize: none;
}

.list-page-crud ::v-deep .el-table .cell {
  padding-top: 4px;
  padding-bottom: 4px;
}
</style>
